import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { authApi } from '@/api/auth.api';
import { formatApiMessage } from '@/api/client';
import { AppShell } from '@/components/layout/AppShell';
import { useAuthStore } from '@/stores/auth.store';

export function SecurityPage() {
  const { t } = useTranslation();
  const setUser = useAuthStore((s) => s.setUser);
  const qc = useQueryClient();

  // Always read 2FA state from /auth/me — that's the source of truth and
  // also picks up changes other devices made.
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
  });
  const twoFaEnabled = me?.twoFaEnabled ?? false;

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="page-title">{t('settings_security.title')}</h1>
        <p className="page-subtitle">{t('settings_security.subtitle')}</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        <div className="card">
          <h3 className="text-base font-semibold text-gray-900 mb-1">{t('settings_security.email_verification')}</h3>
          {me?.isEmailVerified ? (
            <span className="badge bg-success-bg text-success border-emerald-200">{t('settings_security.verified')}</span>
          ) : (
            <span className="badge bg-warning-bg text-warning border-amber-200">{t('settings_security.not_verified')}</span>
          )}
          <p className="text-sm text-muted mt-3">{t('settings_security.email_verification_hint')}</p>
        </div>

        <TwoFaCard
          enabled={twoFaEnabled}
          onChanged={async () => {
            const fresh = await authApi.me();
            if (fresh) setUser(fresh);
            qc.invalidateQueries({ queryKey: ['auth', 'me'] });
          }}
        />

        <div className="card">
          <h3 className="text-base font-semibold text-gray-900 mb-1">{t('settings_security.password_section')}</h3>
          <p className="text-sm text-muted mb-3">{t('settings_security.password_change_hint')}</p>
          <p className="help">{t('settings_security.password_coming_soon')}</p>
        </div>
      </div>
    </AppShell>
  );
}

function TwoFaCard({ enabled, onChanged }: { enabled: boolean; onChanged: () => void }) {
  const { t } = useTranslation();
  const [setupData, setSetupData] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState('');

  const setupMutation = useMutation({
    mutationFn: () => authApi.setup2Fa(),
    onSuccess: (data) => setSetupData({ qrDataUrl: data.qrDataUrl, secret: data.secret }),
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const enableMutation = useMutation({
    mutationFn: () => authApi.enable2Fa(code),
    onSuccess: () => {
      toast.success(t('settings_security.twofa_enabled_toast'));
      setSetupData(null);
      setCode('');
      onChanged();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  const disableMutation = useMutation({
    mutationFn: () => authApi.disable2Fa(code),
    onSuccess: () => {
      toast.success(t('settings_security.twofa_disabled_toast'));
      setCode('');
      onChanged();
    },
    onError: (err) => toast.error(formatApiMessage(err)),
  });

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-base font-semibold text-gray-900 m-0">{t('settings_security.twofa')}</h3>
        {enabled ? (
          <span className="badge bg-success-bg text-success border-emerald-200">
            {t('settings_security.twofa_enabled')}
          </span>
        ) : (
          <span className="badge badge-muted">{t('settings_security.twofa_disabled')}</span>
        )}
      </div>
      <p className="text-sm text-muted mb-3">{t('settings_security.twofa_explain')}</p>

      {!enabled && !setupData && (
        <button
          onClick={() => setupMutation.mutate()}
          disabled={setupMutation.isPending}
          className="btn btn-primary"
        >
          {setupMutation.isPending ? t('common.loading') : t('settings_security.enable_twofa')}
        </button>
      )}

      {!enabled && setupData && (
        <div className="border border-border rounded-md p-4 bg-gray-50">
          <div className="text-[13px] font-semibold text-gray-900 mb-2">
            {t('settings_security.twofa_scan_title')}
          </div>
          <p className="text-[12px] text-muted mb-3">{t('settings_security.twofa_scan_text')}</p>

          <div className="flex flex-col sm:flex-row gap-4 items-start mb-3">
            <img src={setupData.qrDataUrl} alt="QR" className="border border-border rounded bg-white" />
            <div className="flex-1">
              <div className="text-[11px] uppercase font-semibold text-muted mb-1">
                {t('settings_security.twofa_secret_label')}
              </div>
              <code className="text-[12px] bg-white border border-border rounded px-2 py-1 block break-all">
                {setupData.secret}
              </code>
              <p className="text-[11px] text-muted mt-1">{t('settings_security.twofa_secret_help')}</p>
            </div>
          </div>

          <label className="label">{t('settings_security.twofa_code_label')}</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="input max-w-[180px] font-mono text-center tracking-widest"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && code.length === 6) enableMutation.mutate();
            }}
          />
          <div className="flex gap-2 mt-3">
            <button
              onClick={() => enableMutation.mutate()}
              disabled={code.length !== 6 || enableMutation.isPending}
              className="btn btn-primary"
            >
              {enableMutation.isPending ? t('common.loading') : t('settings_security.twofa_confirm')}
            </button>
            <button
              onClick={() => {
                setSetupData(null);
                setCode('');
              }}
              className="btn btn-secondary"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}

      {enabled && (
        <div className="border border-border rounded-md p-4 bg-gray-50">
          <p className="text-[12px] text-muted mb-3">{t('settings_security.twofa_disable_hint')}</p>
          <label className="label">{t('settings_security.twofa_code_label')}</label>
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            className="input max-w-[180px] font-mono text-center tracking-widest"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          />
          <div className="mt-3">
            <button
              onClick={() => disableMutation.mutate()}
              disabled={code.length !== 6 || disableMutation.isPending}
              className="btn btn-secondary text-danger"
            >
              {disableMutation.isPending ? t('common.loading') : t('settings_security.disable_twofa')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
